import { Injectable } from '@angular/core';
import { HarvestActivityReportsApprovedOrRejectedInputDto } from '@proxy/dtos';
import { HarvestActivityReportService } from '@proxy/services';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Constants } from '../../consts/constants';
import { ReportMapperHelper } from '../../helpers/report-mapper.helper';
import { MonitoringService } from '../../services/monitoring.service';
import { LocalDb } from '../local-db';
import { HarvestActivityReport } from '../objects/harvest-activity-report';

@Injectable({ providedIn: 'root' })
export class HarvestActivityReportRepository {
  private readonly HarvestActivityReportsDaysToKeep =
    environment[Constants.HarvestActivityReportsDaysToKeep];

  constructor(
    private readonly _db: LocalDb,
    private readonly _harvestActivityReportService: HarvestActivityReportService,
    private readonly _monitoringService: MonitoringService
  ) { }

  async refreshHarvestActivityReport(): Promise<void> {
    const harvestActivityReportsTable = this._db.harvestActivityReports;
    let reports: HarvestActivityReport[] = [];

    const pendingReports = await firstValueFrom(
      this._harvestActivityReportService.getHarvestsActivityReportWithPendingApproval()
    );

    reports = pendingReports.pagedResult.items.map(dto =>
      ReportMapperHelper.mapSyncDtoToHarvestActivityReport(dto)
    );
    const ids = reports.map(r => r.id);

    const existingReports = await harvestActivityReportsTable.bulkGet(ids);
    const newReports = reports.filter(
      report =>
        !existingReports.some(
          existing =>
            existing?.id === report.id && existing.isAuthorizable === report.isAuthorizable
        )
    );

    if (newReports.length > 0) {
      await harvestActivityReportsTable.bulkPut(newReports);
    }

    const updatedReports = reports.filter(report => {
      const existing = existingReports.find(e => e?.id === report.id && e.isAuthorizable === report.isAuthorizable);

      return existing && existing.status !== report.status;
    });

    if (updatedReports.length > 0) {
      await harvestActivityReportsTable.bulkPut(updatedReports);
    }

    const approvedOrRejectedReports = await this.getAndUpsertHarvestActivityReportsApprovedOrRejected();

    if (pendingReports.success) {
      await this.deleteObsoleteReports(reports, approvedOrRejectedReports);
    }
  }

  async getByFilters(
    teamId: number,
    shiftId: number | null,
    from: Date,
    to: Date
  ): Promise<HarvestActivityReport[]> {
    const reports = await this._db.harvestActivityReports
      .filter(
        report =>
          report.teamId === teamId &&
          (!shiftId || report.shiftId === shiftId) &&
          new Date(report.date) >= from &&
          new Date(report.date) <= to
      )
      .toArray();

    return reports;
  }

  async getById(id: number): Promise<HarvestActivityReport> {
    return await this._db.harvestActivityReports.get(id);
  }

  private async deleteObsoleteReports(
    pendingReports: HarvestActivityReport[],
    approvedOrRejectedReports: HarvestActivityReport[]
  ): Promise<void> {
    const harvestActivityReportsTable = this._db.harvestActivityReports;
    const harvestActivityReportsSyncTable = this._db.harvestActivityReportSyncStates;

    const daysAgo = this.getDaysAgoDate();

    const allExisting = await harvestActivityReportsTable.toArray();
    const allExistingSyncIds = (await harvestActivityReportsSyncTable.toArray()).map(report =>
      report.harvestActivityReportId.toString()
    );

    const validIds = new Set([
      ...pendingReports.map(r => r.id.toString()),
      ...approvedOrRejectedReports.map(r => r.id.toString()),
    ]);

    const toDelete = allExisting
      .filter(
        r =>
          !allExistingSyncIds.includes(r.number.toString()) &&
          (new Date(r.date) < daysAgo || !validIds.has(r.id.toString()))
      )
      .map(r => r.id);

    if (toDelete.length > 0) {
      await harvestActivityReportsTable.bulkDelete(toDelete);
    }
  }

  private async getAndUpsertHarvestActivityReportsApprovedOrRejected(): Promise<
    HarvestActivityReport[]
  > {
    try {
      const to = new Date();
      const from = this.getDaysAgoDate();

      const input: HarvestActivityReportsApprovedOrRejectedInputDto = {
        from: from.toLocalISOString(),
        to: to.toLocalISOString(),
      };

      const reports = await firstValueFrom(
        this._harvestActivityReportService.getHarvestActivityReportsApprovedOrRejectedByInput(input)
      );

      const mappedReports = reports.map(report =>
        ReportMapperHelper.mapHarvestActivityReportDtoToHarvestActivityReport(report)
      );

      if (mappedReports.length > 0) {
        await this._db.harvestActivityReports.bulkPut(mappedReports);
      }

      return mappedReports;
    } catch (error) {
      this._monitoringService.logException(error);
    }
  }

  private getDaysAgoDate(): Date {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const daysAgo = new Date(now);
    daysAgo.setDate(now.getDate() - this.HarvestActivityReportsDaysToKeep);

    return daysAgo;
  }
}
